import React, { useCallback, useEffect, useState } from "react";
import materiaVideosService from "../../../../services/materiaVideosService";
import { showToast } from "../../../../utils/showToast";

const filaVacia = () => ({ id: null, orden: 1, titulo: "", video_url: "", activo: true });

export default function MateriaVideosManager({ materiaId }) {
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nuevaFila, setNuevaFila] = useState(null);
  const [guardandoId, setGuardandoId] = useState(null);
  const [eliminandoId, setEliminandoId] = useState(null);

  const cargar = useCallback(async () => {
    setLoading(true);
    try {
      const data = await materiaVideosService.listar(materiaId);
      setVideos(Array.isArray(data) ? data : []);
    } catch (err) {
      showToast({ type: "danger", message: err?.message || "No se pudieron cargar los videos." });
    } finally {
      setLoading(false);
    }
  }, [materiaId]);

  useEffect(() => { cargar(); }, [cargar]);

  const validarUrl = (url) => {
    if (!url?.trim()) return "Ingresa el link del video.";
    if (!/^https?:\/\//i.test(url.trim())) return "El link debe ser una URL http(s) válida.";
    return null;
  };

  const handleAgregar = () => {
    const siguienteOrden = videos.length ? Math.max(...videos.map((v) => v.orden)) + 1 : 1;
    setNuevaFila({ ...filaVacia(), orden: siguienteOrden });
  };

  const handleGuardarNueva = async () => {
    const error = validarUrl(nuevaFila.video_url);
    if (error) {
      showToast({ type: "warning", message: error });
      return;
    }
    setGuardandoId("nueva");
    try {
      await materiaVideosService.crear(materiaId, {
        orden: nuevaFila.orden,
        titulo: nuevaFila.titulo?.trim() || null,
        video_url: nuevaFila.video_url.trim(),
      });
      showToast({ type: "success", message: "Video agregado." });
      setNuevaFila(null);
      cargar();
    } catch (err) {
      showToast({ type: "danger", message: err?.message || "No se pudo agregar el video." });
    } finally {
      setGuardandoId(null);
    }
  };

  const handleCambiarFila = (id, campo, valor) => {
    setVideos((prev) => prev.map((v) => (v.id === id ? { ...v, [campo]: valor } : v)));
  };

  const handleGuardarFila = async (video) => {
    const error = validarUrl(video.video_url);
    if (error) {
      showToast({ type: "warning", message: error });
      return;
    }
    setGuardandoId(video.id);
    try {
      await materiaVideosService.actualizar(video.id, {
        orden: video.orden,
        titulo: video.titulo?.trim() || null,
        video_url: video.video_url.trim(),
        activo: video.activo,
      });
      showToast({ type: "success", message: "Video actualizado." });
      cargar();
    } catch (err) {
      showToast({ type: "danger", message: err?.message || "No se pudo actualizar el video." });
    } finally {
      setGuardandoId(null);
    }
  };

  const handleEliminar = async (id) => {
    setEliminandoId(id);
    try {
      await materiaVideosService.eliminar(id);
      showToast({ type: "success", message: "Video eliminado." });
      cargar();
    } catch (err) {
      showToast({ type: "danger", message: err?.message || "No se pudo eliminar el video." });
    } finally {
      setEliminandoId(null);
    }
  };

  return (
    <div className="border rounded-3 p-3 bg-light-subtle">
      <div className="d-flex justify-content-between align-items-start mb-2">
        <div>
          <p className="fw-semibold mb-1">
            <i className="bi bi-camera-video me-1" />
            Videos de respaldo (Campus)
          </p>
          <small className="text-muted d-block">
            Se cargan <strong>una sola vez por materia</strong> y aplican a todas sus promociones —
            si son varios capítulos/módulos, agrégalos todos: en Campus se muestran juntos en
            cualquier fecha de esa materia que no tenga video propio del docente, sin importar
            cuántas fechas tenga esa materia en cada cronograma.
          </small>
        </div>
        <button
          type="button"
          className="btn btn-outline-primary btn-sm text-nowrap"
          onClick={handleAgregar}
          disabled={!!nuevaFila}
        >
          <i className="bi bi-plus-circle me-1" />
          Agregar capítulo
        </button>
      </div>

      {loading ? (
        <div className="text-center py-3">
          <span className="spinner-border spinner-border-sm" />
        </div>
      ) : (
        <div className="table-responsive">
          <table className="table table-sm align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th style={{ width: 60 }}>Orden</th>
                <th>Título (opcional)</th>
                <th>Link del video</th>
                <th style={{ width: 70 }} className="text-center">Activo</th>
                <th style={{ width: 90 }}></th>
              </tr>
            </thead>
            <tbody>
              {videos.length === 0 && !nuevaFila && (
                <tr>
                  <td colSpan={5} className="text-center text-muted py-3">
                    Sin videos configurados — esta materia usará el que suba el docente por sesión.
                  </td>
                </tr>
              )}
              {videos.map((v) => (
                <tr key={v.id} className={!v.activo ? "opacity-50" : ""}>
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      style={{ width: 60 }}
                      min={1}
                      value={v.orden}
                      onChange={(e) => handleCambiarFila(v.id, "orden", Number(e.target.value) || 1)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Ej: Capítulo 1 - Introducción"
                      value={v.titulo || ""}
                      onChange={(e) => handleCambiarFila(v.id, "titulo", e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      type="url"
                      className="form-control form-control-sm"
                      placeholder="https://youtube.com/..."
                      value={v.video_url || ""}
                      onChange={(e) => handleCambiarFila(v.id, "video_url", e.target.value)}
                    />
                  </td>
                  <td className="text-center">
                    <div className="form-check form-switch d-flex justify-content-center mb-0">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={!!v.activo}
                        onChange={(e) => handleCambiarFila(v.id, "activo", e.target.checked)}
                      />
                    </div>
                  </td>
                  <td>
                    <div className="d-flex gap-1">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-primary"
                        title="Guardar"
                        onClick={() => handleGuardarFila(v)}
                        disabled={guardandoId === v.id}
                      >
                        {guardandoId === v.id ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-floppy" />}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-danger"
                        title="Eliminar"
                        onClick={() => handleEliminar(v.id)}
                        disabled={eliminandoId === v.id}
                      >
                        {eliminandoId === v.id ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-trash" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {nuevaFila && (
                <tr className="table-primary bg-opacity-25">
                  <td>
                    <input
                      type="number"
                      className="form-control form-control-sm"
                      style={{ width: 60 }}
                      min={1}
                      value={nuevaFila.orden}
                      onChange={(e) => setNuevaFila((f) => ({ ...f, orden: Number(e.target.value) || 1 }))}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Ej: Capítulo 1 - Introducción"
                      value={nuevaFila.titulo}
                      onChange={(e) => setNuevaFila((f) => ({ ...f, titulo: e.target.value }))}
                    />
                  </td>
                  <td>
                    <input
                      type="url"
                      className="form-control form-control-sm"
                      placeholder="https://youtube.com/..."
                      value={nuevaFila.video_url}
                      onChange={(e) => setNuevaFila((f) => ({ ...f, video_url: e.target.value }))}
                      autoFocus
                    />
                  </td>
                  <td className="text-center text-muted small">—</td>
                  <td>
                    <div className="d-flex gap-1">
                      <button
                        type="button"
                        className="btn btn-sm btn-primary"
                        title="Guardar"
                        onClick={handleGuardarNueva}
                        disabled={guardandoId === "nueva"}
                      >
                        {guardandoId === "nueva" ? <span className="spinner-border spinner-border-sm" /> : <i className="bi bi-check-lg" />}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline-secondary"
                        title="Cancelar"
                        onClick={() => setNuevaFila(null)}
                      >
                        <i className="bi bi-x-lg" />
                      </button>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
